/**
 * Email Queue API module.
 *
 * Provides endpoints for managing the email queue system including
 * queue entries, stats, cancel/retry actions, and template previews.
 */
import api from './client';
import type { PaginatedResponse } from './client';

export interface EmailQueueEntry {
  id: number;
  recipient_email: string;
  subject: string;
  body_preview: string;
  status: 'pending' | 'sending' | 'sent' | 'failed' | 'cancelled' | 'skipped';
  notification_type: string;
  priority: string;
  retry_count: number;
  max_retries: number;
  error_message: string;
  batch_id: string;
  created_at: string;
  sent_at: string | null;
  cancelled_at: string | null;
}

export interface EmailQueueStats {
  pending: number;
  sending: number;
  sent_24h: number;
  failed: number;
  cancelled: number;
  skipped: number;
}

export interface EmailTemplateType {
  value: string;
  label: string;
  category: string;
}

export const emailQueueAPI = {
  /** Fetch paginated queue entries with optional filters. */
  getEntries: (params?: Record<string, string | number | boolean>) =>
    api.get<PaginatedResponse<EmailQueueEntry>>('/admin/email/email-queue/', { params }),

  /** Fetch a single queue entry by ID. */
  getEntry: (id: number) =>
    api.get<EmailQueueEntry>(`/admin/email/email-queue/${id}/`),

  /** Fetch aggregate stats for the queue dashboard. */
  getStats: () =>
    api.get<EmailQueueStats>('/admin/email/email-queue/stats/'),

  /** Cancel a single pending queue entry. */
  cancelEntry: (id: number) =>
    api.post(`/admin/email/email-queue/${id}/cancel/`),

  /** Cancel all entries in a batch. */
  cancelBatch: (batchId: string) =>
    api.post('/admin/email/email-queue/cancel-batch/', { batch_id: batchId }),

  /** Cancel all pending entries in the queue. */
  cancelAll: () =>
    api.post('/admin/email/email-queue/cancel-all/'),

  /** Retry a single failed queue entry. */
  retryEntry: (id: number) =>
    api.post(`/admin/email/email-queue/${id}/retry/`),

  /** Retry all failed entries in the queue. */
  retryAll: () =>
    api.post('/admin/email/email-queue/retry-all/'),

  /** Preview a branded email template by notification type. */
  previewTemplate: (type: string) =>
    api.get<{ html: string }>('/admin/email/email-templates/preview/', { params: { type } }),

  /** List all available notification template types. */
  listTemplateTypes: () =>
    api.get<Record<string, EmailTemplateType[]>>('/admin/email/email-templates/types/'),
};
